import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import { PowerfulERC20Client } from '../utils/ndeipiCoinClient';
import { getCurrentSigner } from '../components/features/signingDependencies';
import { Transaction } from '../models/Transaction';
import axios from '../utils/axios';
import { getAssociatedTokenAddress, getAccount, getMint } from '@solana/spl-token';
import {setWalletAddress} from './userSlice';
import { setWalletSection } from './uiSlice';
import  AxiosInstance  from 'axios';

import { PublicKey, Connection } from '@solana/web3.js';
import { getUsersAssetBalance } from './assetSlice';

export interface TokenItem {
  id: string;
  name: string;
  symbol: string;
  balance: string;
  isChecked: boolean;
  icon?: string;
  isSelected?: boolean;
  assetId?: string;
  address?: string;
}

interface WalletState {
    //walletAddress: string;
    privateKey: string;
    publicKey: string;
    loading: boolean;
    error: string | null;
    walletDialogOpen: boolean;
    name: string;
  symbol: string;
  decimals: number ;
  totalSupply: string;
  balance: string;
  provider: ethers.JsonRpcProvider | null;
  transactions: Transaction[] | null;
  donations: Transaction[] | null;
  lastFetched: Date| null;
  pagination: {
    page: number,
    pageSize: number,
    hasMore: boolean
  }
  chainType: string; // Added chainType to differentiate between EVM and Solana
  giftTokenBalance: string;
  selectedToken: TokenItem | null;
}

export interface TokenBalanceDto {
  result: {
    balance: string;
    address: string;
    formattedBalance: string;
  }
}



const initialState: WalletState = {
    // walletAddress: '',
    privateKey: '',
    publicKey: '',
    loading: false,
    error: null,
    walletDialogOpen: false,
    name: '',
    symbol: '',
    decimals: 18, // Default to 18 decimals
    totalSupply: '0', // Default total supply
    balance:  '0', // Default balance
    provider: null,// Optional signer for transactions
    transactions: null, //
    donations: null, //
    lastFetched: null,
    chainType: 'solana', 
  pagination: {
    page: 1,
    pageSize: 10,
    hasMore: true
  },
  giftTokenBalance: '0',
  selectedToken: {
        id: "ndeipi",
        name: "Ndeipi Token",
        symbol: "NDEIPI",
        balance: "0.000", // Use actual balance
        isChecked: false,
        isSelected: true,
        address: process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS || '0xD8d401df77080Baa60718FA0b1f775eea272ab3b',
      },  
};

const getProviderAndSigner = (privateKey: string) => {
  const provider = new ethers.JsonRpcProvider(process.env.REACT_APP_POLYGON_RPC_URL || 'https://polygon-rpc.com/');
  const signer = new ethers.Wallet(privateKey, provider);
  return { provider, signer };
};

export const initializeToken = createAsyncThunk<
  { address: string; name: string; symbol: string; decimals: number; totalSupply: string; },
  void,
  { rejectValue: string; state: { wallet: WalletState } }
>(
  'token/initialize',
  async (_, { rejectWithValue, getState }) => {
    try {
      console.log("**Initializing token...");
      const signer = getCurrentSigner();

      if (!signer) {
        throw new Error("No valid signer available");
      }
      const stateSigner = signer!;
      const privateKey = stateSigner instanceof ethers.Wallet ? stateSigner.privateKey : getState().wallet.privateKey;

      const tokenAddress = process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS || '0xYourTokenAddressHere';


      const signerCandidate = stateSigner !== undefined ? stateSigner : getProviderAndSigner(privateKey).signer;
      if (!signerCandidate) {
        throw new Error("No valid signer available");
      }
      const tokenClient = new PowerfulERC20Client(tokenAddress, signerCandidate);

      const [name, symbol, decimals, totalSupply] = await Promise.all([
        tokenClient.getName(),
        tokenClient.getSymbol(),
        18, // tokenClient.getDecimals(),
        tokenClient.getTotalSupply(),
      ]);

      return {
        address: tokenAddress,
        name,
        symbol,
        decimals,
        totalSupply: ethers.formatUnits(totalSupply, decimals)

      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBalance = createAsyncThunk<
  string,
  string,
  { rejectValue: string; state: { wallet: WalletState } }
>(
  'token/fetchBalance',
  async (walletAddress, { getState, rejectWithValue }) => {
    try {
      const { privateKey, chainType } = getState().wallet;
      const stateSigner = getCurrentSigner();

      // Solana balance fetch
      if (chainType === 'solana') {
        const connection = new Connection(
          process.env.REACT_APP_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com'
        );
        

        // MOVEUSD token mint address
        const moveUsdMintAddress = process.env.REACT_APP_MOVEUSD_TOKEN_ADDRESS || 'MOVEUSDmwh...';
        console.log("**Using MOVEUSD mint address:", moveUsdMintAddress);
        const moveUsdMint = new PublicKey(moveUsdMintAddress);
        console.log("**Using Solana RPC walletAddress:", walletAddress);
        // User's public key
        const userPublicKey = new PublicKey(walletAddress);

        // Find the associated token account
        const associatedTokenAccount = await getAssociatedTokenAddress(
          moveUsdMint,
          userPublicKey
        );

        try {
          // Get the token account info
          const tokenAccount = await getAccount(connection, associatedTokenAccount);
          const mintInfo = await getMint(connection, moveUsdMint);
          
          // Calculate balance with decimals
          const balance = Number(tokenAccount.amount) / Math.pow(10, mintInfo.decimals);
          return balance.toString();
        } catch (error) {
          // Return 0 if account doesn't exist
          if (error instanceof Error && error.message.includes('could not find account')) {
            return '0';
          }
          throw error;
        }
      } 
      // EVM (Polygon) balance fetch
      else {
        const tokenAddress = process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS || '0xYourTokenAddressHere';
        const signer = stateSigner !== undefined ? stateSigner! : getProviderAndSigner(privateKey).signer;
        
        if (!signer) {
          throw new Error("No valid signer available");
        }

        const tokenClient = new PowerfulERC20Client(tokenAddress, signer);
        const balance = await tokenClient.getBalanceOf(walletAddress);
        const decimals = getState().wallet.decimals;
        console.log("Decimals:", decimals);
        return ethers.formatUnits(balance, decimals);
      }
    } catch (error: any) {
      console.error('Error fetching balance:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const transferTokens = createAsyncThunk<
  { recipient: string; amount: string },
  { recipient: string; amount: string },
  { rejectValue: string; state: { wallet: WalletState } }
>(
  'token/transfer',
  async ({ recipient, amount }, { getState, rejectWithValue }) => {
    try {
      const stateSigner = getCurrentSigner();
      const { privateKey } = getState().wallet;
      const tokenAddress = process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS || '0xYourTokenAddressHere';
      const signer = stateSigner !== undefined ? stateSigner! : getProviderAndSigner(privateKey).signer;
      if (!signer) {
        throw new Error("No valid signer available");
      }
      const tokenClient = new PowerfulERC20Client(tokenAddress, signer);

      const parsedAmount = await tokenClient.parseTokenAmount(amount);
      const tx = await tokenClient.transfer(recipient, parsedAmount);
      await tx.wait();

      return { recipient, amount };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const transferCollectibleAssets = createAsyncThunk<
  { recipient: string; amount: string },
  { recipient: string; amount: string },{ rejectValue: string; state: { wallet: WalletState } }
>(
  'token/transferCollectibleAssets',
  async ({ recipient, amount }, { getState, rejectWithValue,dispatch }) => {
    try {
     
      const selectedTokenItem = getState().wallet.selectedToken;
      if (!selectedTokenItem) {
        throw new Error("No token selected for transfer");
      }else{
        console.log("**Transferring token:", selectedTokenItem);
        console.log("**To recipient:", recipient);
        console.log("**Amount:", amount);
        const response = await axios.post("api/services/app/Asset/TransferTokens", {
            destinationAccount: recipient,
            tokenId: selectedTokenItem.assetId,
            amount: amount
        }); 
        
        dispatch(getUsersAssetBalance())
        return response.data.result;
      }
     
      //return { recipient, amount };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

//Fetch Transactions
interface FetchTransactionsParams {
  accountAddress: string; 
  contractAddress?: string; 
  page?: number;
  offset?: number;
  sort?: 'asc' | 'desc';
}

export const fetchTransactions = createAsyncThunk<
  Transaction[],
  FetchTransactionsParams,
  { rejectValue: string }
>(
  'transactions/fetchTransactions',
  async (
    {
      accountAddress,
      contractAddress,
      page = 1,
      offset = 10,
      sort = 'desc'
    },
    { rejectWithValue }
  ) => {
    try {

      const {
        REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS,
        REACT_APP_ETHERSCAN_API_KEY,
        // REACT_APP_POLYGONSCAN_API_KEY,
        // REACT_APP_POLYGON_SCAN_URL
      } = process.env

      // console.log("**Fetching transactions for address:", accountAddress);

      

      //const contractAddress = REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS;
      const apiKey = REACT_APP_ETHERSCAN_API_KEY;
      // const apiUrl = REACT_APP_POLYGON_SCAN_URL || 'https://api.polygonscan.com/api';

      // console.log("**Fetching transactions for contract:", contractAddress);

      // console.log("Environment Variables:", {
      //   contractAddress,
      //   apiKey: apiKey ? '***REDACTED***' : 'MISSING',
      //   apiUrl
      // });

      if (!apiKey) {
        throw new Error('PolygonScan API key not configured');
      }

      if (!REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS) {
        throw new Error('Token contract address not configured');
      }

      if (!REACT_APP_ETHERSCAN_API_KEY) {
        throw new Error('Etherscan API key not configured');
      }

      if (!contractAddress) {
        throw new Error('Token contract address is required');
      }

      // console.log("**Using Etherscan API key:", apiKey);

      const response = await AxiosInstance.get(
        process.env.REACT_APP_ETHERSCAN_API_URL || 'https://api.etherscan.io/v2/api?chainid=137',
        {
          params: {
            module: 'account',
            action: 'tokentx',
            contractAddress: contractAddress,
            address: accountAddress,
            page,
            offset,
            sort,
            apikey: apiKey
          }
        }
      );

      

      const { data } = response;

      if (data.status !== '1') {
        throw new Error(data.message || 'Failed to fetch transactions from PolygonScan');
      }

      return data.result.map((tx: any): Transaction => {
        const isSent = tx.from.toLowerCase() === accountAddress.toLowerCase();
        const isReceived = tx.to.toLowerCase() === accountAddress.toLowerCase();
        let txType: Transaction['type'] = 'contract';

        // console.log("**Transaction details value:", tx.value );
        
        if (isSent) txType = 'sent';
        if (isReceived) txType = 'received';

        return {
          hash: tx.hash,
          from: tx.from,
          to: tx.to,
          timestamp: parseInt(tx.timeStamp, 10),
          value: (Number(tx.value)/ Math.pow(10, parseInt(tx.tokenDecimal, 10))).toString(), 
          tokenSymbol: tx.tokenSymbol,
          tokenDecimal: parseInt(tx.tokenDecimal, 10),
          gasUsed: tx.gasUsed,
          gasPrice: tx.gasPrice,
          status: Number(tx.confirmations) > 0 ? 'success' : 'failed',
          type: txType,
          blockNumber: parseInt(tx.blockNumber, 10),
          confirmations: parseInt(tx.confirmations, 10),
        };
      });
    } catch (error: any) {
      if (error.response) {
        // The request was made and the server responded with a status code
        return rejectWithValue(error.response.data.message || 'PolygonScan API error');
      } else if (error.request) {
        // The request was made but no response was received
        return rejectWithValue('No response from PolygonScan API');
      } else {
        // Something happened in setting up the request
        return rejectWithValue(error.message || 'Error fetching transactions');
      }
    }
  }
);

export const createWallet = createAsyncThunk<string, string, { state: { wallet: WalletState } }>(
  'wallet/createWallet',
  async (_, { rejectWithValue, getState,dispatch }) => {

  const { chainType: currentChainType } = getState().wallet; 
    try {
      if( currentChainType !== 'evm' && currentChainType !== 'solana') {
        return rejectWithValue('Invalid chain type. Must be "evm" or "solana".');
      }
      if (currentChainType === 'solana') {
        const solanaResponse = await axios.post('api/services/app/Customers/CreateSolanaWalletForUser', {
      });
       dispatch(setWalletAddress(solanaResponse.data.result));
       dispatch(setWalletSection('main'));
      return solanaResponse.data.result;
      }
      if (currentChainType === 'evm') {
        const evmResponse = await axios.post('api/services/app/Customers/CreatePolygonWalletForUser', {
      });
      dispatch(setWalletAddress(evmResponse.data.result));
      dispatch(setWalletSection('main'));
      return evmResponse.data.result;
      }

    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

//SetCustomerWalletAddressAsync
export const setUserWalletAddress = createAsyncThunk<string, string>(
  'wallet/setUserWalletAddress',
  async (walletAddress:string, { rejectWithValue,dispatch }) => {
    try {
      
        const updateResponse = await axios.post(`api/services/app/Customers/SetCustomerWalletAddress?input=${walletAddress}`);
      
        console.log('Update Response ===> ',updateResponse)
        dispatch(setWalletAddress(updateResponse.data.result));
       dispatch(setWalletSection('main'));
      
        return updateResponse.data.result;
      
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getGiftTokenBalance = createAsyncThunk(
    "token/getGiftTokenBalance",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<TokenBalanceDto>(
                "api/services/app/GiftToken/GetBalance"
            );

            return response.data.result;
        } catch (error:any) {
            return rejectWithValue({
                message: error.message || "An unknown error occurred",
                code: "UNKNOWN_ERROR"
            });
        }
    }
);

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        // setWalletAddress: (state, action: PayloadAction<string>) => {
        //     state.walletAddress = action.payload;
        // },
        setPrivateKey: (state, action: PayloadAction<string>) => {
            state.privateKey = action.payload;
        },
        setPublicKey: (state, action: PayloadAction<string>) => {
            state.publicKey = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setWalletDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.walletDialogOpen = action.payload;
        },
        setChainType: (state, action: PayloadAction<string>) => {
          // console.log("**Setting chain type:", action.payload);
            state.chainType = action.payload;
        },
        setSelectedToken: (state, action: PayloadAction<TokenItem | null>) => {
          state.selectedToken = action.payload;
      }, setSelectedTokenBalance: (state, action: PayloadAction<string>) => {
        if (state.selectedToken) {
          state.selectedToken.balance = action.payload;
        }}
    },
    extraReducers: (builder) => {
         builder
        .addCase(initializeToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeToken.fulfilled, (state, action) => {
        state.loading = false;
        // state.walletAddress = action.payload.address;
        state.name = action.payload.name;
        state.symbol = action.payload.symbol;
        state.decimals = action.payload.decimals;
        state.totalSupply = action.payload.totalSupply;
        
      })
      .addCase(initializeToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('Error initializing token:', action.payload);
      })

      // Fetch Balance
      .addCase(fetchBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('Error fetching balance:', action.payload);
      })

      // Transfer Tokens
      .addCase(transferTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferTokens.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(transferTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      //Fetch Transactions
      builder.addCase(fetchTransactions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions = action.payload;
      // console.log("**Fetched transactions:", action.payload);
      
    });
    builder.addCase(fetchTransactions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to fetch transactions';
      //console.error('Error fetching transactions:', action.payload);
    }).addCase(getGiftTokenBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
    }).addCase(getGiftTokenBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.giftTokenBalance = action.payload.balance;
    }).addCase(getGiftTokenBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('Error fetching gift token balance:', action.payload)
    }).addCase(transferCollectibleAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferCollectibleAssets.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(transferCollectibleAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    }
});

export const {
    // setWalletAddress,
    setPrivateKey,
    setPublicKey,
    setLoading,
    setError,
    setWalletDialogOpen,
    setChainType,
    setSelectedToken,
    setSelectedTokenBalance
} = walletSlice.actions;

export default walletSlice.reducer;
